import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { colors, status } from '../../theme';

const STATE_TONE = {
  active: status.positive,
  queued: status.warning,
  'worker-offline': status.negative,
  idle: status.neutral,
};

const CATEGORY_TONE = {
  morning: status.info,
  intraday: status.warning,
  market_close: status.purple,
  evening: status.cyan,
  recurring: status.neutral,
};

function StateIndicator({ state, label }) {
  const tone = STATE_TONE[state] || status.neutral;
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: tone.main,
          boxShadow: state === 'active' ? `0 0 0 3px ${tone.bg}` : 'none',
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tone.text }}>
        {label}
      </Typography>
    </Stack>
  );
}

StateIndicator.propTypes = {
  state: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

function CategoryPill({ categoryKey, label }) {
  const tone = CATEGORY_TONE[categoryKey] || status.neutral;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1,
        borderRadius: 1,
        bgcolor: tone.bg,
        color: tone.text,
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: 0.1,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

CategoryPill.propTypes = {
  categoryKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

function AutomationJobsTable({ jobs, onTrigger, pendingJobKey, t }) {
  const headSx = {
    fontWeight: 700,
    fontSize: 10.5,
    color: colors.gray[500],
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    bgcolor: colors.gray[50],
    borderBottom: `1px solid ${colors.gray[200]}`,
    py: 0.85,
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 0,
        boxShadow: 'none',
        overflow: 'visible',
        '& .MuiTableCell-root': {
          py: 1,
          px: 1.5,
          verticalAlign: 'middle',
          borderBottomColor: colors.gray[100],
        },
        '& .MuiTableBody-root .MuiTableRow-root:hover': {
          bgcolor: colors.gray[50],
        },
        '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
          borderBottom: 'none',
        },
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>{t('automation.jobs.job', 'Job')}</TableCell>
            <TableCell sx={headSx}>{t('automation.jobs.schedule', 'Schedule')}</TableCell>
            <TableCell sx={headSx}>{t('automation.jobs.category', 'Category')}</TableCell>
            <TableCell sx={headSx}>{t('automation.jobs.state', 'State')}</TableCell>
            <TableCell sx={headSx}>{t('automation.jobs.worker', 'Worker')}</TableCell>
            <TableCell align="right" sx={headSx}>{t('automation.jobs.action', 'Action')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => {
            const pending = pendingJobKey === job.key;
            return (
              <TableRow key={job.key} hover>
                {/* Job */}
                <TableCell sx={{ minWidth: 260 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: colors.gray[800], fontSize: 13.5, lineHeight: 1.3 }}
                  >
                    {job.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 11,
                      color: colors.gray[500],
                      lineHeight: 1.3,
                    }}
                  >
                    {job.task_name}
                  </Typography>
                </TableCell>

                {/* Schedule */}
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 130 }}>
                  <Typography
                    sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: colors.gray[700],
                    }}
                  >
                    {job.schedule_label}
                  </Typography>
                  {!job.enabled && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.15,
                        color: status.warning.text,
                        fontSize: 10.5,
                        fontWeight: 600,
                      }}
                    >
                      {t('automation.jobs.disabled', 'Beat disabled')}
                    </Typography>
                  )}
                </TableCell>

                {/* Category */}
                <TableCell>
                  <CategoryPill categoryKey={job.category_key || 'other'} label={job.category_label} />
                </TableCell>

                {/* State */}
                <TableCell>
                  <Stack spacing={0.3}>
                    <StateIndicator state={job.state} label={job.state_label} />
                    {(job.active_count > 0 || job.queued_count > 0) && (
                      <Typography
                        variant="caption"
                        sx={{ color: colors.gray[500], fontSize: 10.5, pl: 1.85 }}
                      >
                        {job.active_count}↑ {job.queued_count}⌛
                      </Typography>
                    )}
                  </Stack>
                </TableCell>

                {/* Worker */}
                <TableCell>
                  <Stack spacing={0.3}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: job.worker_registered ? status.positive.main : status.warning.main,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: job.worker_registered ? status.positive.text : status.warning.text,
                        }}
                      >
                        {job.worker_registered
                          ? t('automation.jobs.registered', 'Registered')
                          : t('automation.jobs.unregistered', 'Missing')}
                      </Typography>
                    </Stack>
                    {job.flow_keys?.length > 0 && (
                      <Stack direction="row" spacing={0.4} sx={{ pl: 1.85, flexWrap: 'wrap', gap: 0.3 }} useFlexGap>
                        {job.flow_keys.map((flowKey) => (
                          <Chip
                            key={flowKey}
                            size="small"
                            label={flowKey}
                            sx={{
                              height: 16,
                              fontSize: 10,
                              fontWeight: 600,
                              bgcolor: colors.gray[100],
                              color: colors.gray[600],
                              '& .MuiChip-label': { px: 0.65 },
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </TableCell>

                {/* Action */}
                <TableCell align="right">
                  <Tooltip title={t('automation.jobs.triggerHint', 'Queue this job to the worker now')} arrow>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={pending
                          ? <CircularProgress size={12} thickness={5} sx={{ color: status.info.text }} />
                          : <RocketLaunchIcon sx={{ fontSize: 14 }} />}
                        onClick={() => onTrigger(job.key)}
                        disabled={pending}
                        sx={{
                          whiteSpace: 'nowrap',
                          borderRadius: 1.5,
                          boxShadow: 'none',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: 12.5,
                          py: 0.4,
                          px: 1.25,
                          minWidth: 92,
                          color: status.info.text,
                          borderColor: colors.gray[200],
                          bgcolor: '#fff',
                          '&:hover': {
                            bgcolor: status.info.bg,
                            borderColor: status.info.border,
                            boxShadow: 'none',
                          },
                          '&.Mui-disabled': {
                            color: status.info.text,
                            borderColor: status.info.border,
                            bgcolor: status.info.bg,
                            opacity: 0.9,
                          },
                        }}
                      >
                        {pending
                          ? t('automation.jobs.queueing', 'Queueing…')
                          : t('automation.jobs.trigger', 'Trigger')}
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

AutomationJobsTable.propTypes = {
  jobs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      task_name: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired,
      schedule_label: PropTypes.string.isRequired,
      category_key: PropTypes.string,
      category_label: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      state_label: PropTypes.string.isRequired,
      active_count: PropTypes.number.isRequired,
      queued_count: PropTypes.number.isRequired,
      worker_registered: PropTypes.bool.isRequired,
      flow_keys: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  onTrigger: PropTypes.func.isRequired,
  pendingJobKey: PropTypes.string,
  t: PropTypes.func.isRequired,
};

AutomationJobsTable.defaultProps = {
  pendingJobKey: null,
};

export default AutomationJobsTable;